import type { JSX } from 'solid-js'
import { createMemo, createSignal, createUniqueId, mergeProps, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import FieldMessage from '~/components/FieldMessage'
import { useFieldError } from '~/hooks/useFieldError'
import type { RadioColorType, RadioPropsType, RadioSizeType } from '~/types/Radio'

type SizeKey = 'sm' | 'md' | 'lg'

const sizeClasses = {
  sm: {
    container: 'h-4 w-4',
    dot: 'h-1.5 w-1.5',
    label: 'text-sm',
    spacing: 'gap-2'
  },
  md: {
    container: 'h-5 w-5',
    dot: 'h-2 w-2',
    label: 'text-base',
    spacing: 'gap-2.5'
  },
  lg: {
    container: 'h-6 w-6',
    dot: 'h-2.5 w-2.5',
    label: 'text-lg',
    spacing: 'gap-3'
  }
}

const colorClasses: Record<string, { base: string; hover: string; checked: string; dot: string }> = {
  primary: {
    base: 'border-border-strong',
    hover: 'group-hover:border-primary-500 dark:group-hover:border-primary-700',
    checked: 'border-primary',
    dot: 'bg-primary'
  },
  secondary: {
    base: 'border-border-strong',
    hover: 'group-hover:border-slate-500 dark:group-hover:border-slate-400',
    checked: 'border-foreground-muted',
    dot: 'bg-inverse'
  },
  success: {
    base: 'border-border-strong',
    hover: 'group-hover:border-green-500 dark:group-hover:border-green-400',
    checked: 'border-success',
    dot: 'bg-success'
  },
  danger: {
    base: 'border-border-strong',
    hover: 'group-hover:border-red-500 dark:group-hover:border-red-400',
    checked: 'border-danger',
    dot: 'bg-danger'
  },
  warning: {
    base: 'border-border-strong',
    hover: 'group-hover:border-amber-500 dark:group-hover:border-amber-400',
    checked: 'border-warning',
    dot: 'bg-warning'
  },
  info: {
    base: 'border-border-strong',
    hover: 'group-hover:border-blue-500 dark:group-hover:border-blue-400',
    checked: 'border-info',
    dot: 'bg-info'
  },
  default: {
    base: 'border-border-strong',
    hover: 'group-hover:border-slate-500 dark:group-hover:border-slate-400',
    checked: 'border-foreground-muted',
    dot: 'bg-inverse'
  }
}

export default function Radio(props: RadioPropsType): JSX.Element {
  const merged = mergeProps(
    {
      size: 'md' as RadioSizeType,
      color: 'primary' as RadioColorType
    },
    props as Partial<RadioPropsType>
  )

  const uid = createUniqueId()

  const [local, others] = splitProps(merged, ['class', 'classList', 'label', 'color', 'size', 'error', 'helperText', 'onChange'])

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const resolvedError = useFieldError(
    () => (others.name ? String(others.name) : undefined),
    () => local.error
  )
  const hasError = () => Boolean(resolvedError())

  let inputRef: HTMLInputElement | undefined
  const [isCheckedSignal, setIsCheckedSignal] = createSignal(false)

  const isChecked = () => {
    if (others.checked !== undefined) {
      return others.checked
    }
    return isCheckedSignal()
  }

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    setIsCheckedSignal(target.checked)

    if (local.onChange) {
      local.onChange(e)
    }
  }

  onMount(() => {
    if (!inputRef) return

    setIsCheckedSignal(inputRef.checked)

    inputRef.addEventListener('input', () => {
      setIsCheckedSignal(inputRef?.checked)
    })

    if (inputRef.name) {
      const handleGroupChange = (e: Event) => {
        const target = e.target as HTMLInputElement
        if (target.name === inputRef?.name && target !== inputRef) {
          setIsCheckedSignal(inputRef?.checked)
        }
      }

      document.addEventListener('input', handleGroupChange)

      onCleanup(() => {
        document.removeEventListener('input', handleGroupChange)
      })
    }
  })

  const currentColorClasses = createMemo(() => colorClasses[local.color as string])

  const currentSizeClasses = createMemo(() => sizeClasses[local.size as SizeKey])

  const containerClasses = createMemo(() => {
    const size = currentSizeClasses()
    const color = currentColorClasses()
    const checked = isChecked()

    return twMerge(
      'relative inline-flex shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200',
      size.container,
      hasError() ? 'border-danger' : checked ? color.checked : color.base,
      !hasError() && !checked ? color.hover : '',
      checked ? '' : 'bg-surface',
      others.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      local.class || ''
    )
  })

  const dotClasses = createMemo(() => {
    const size = currentSizeClasses()
    const color = currentColorClasses()
    const checked = isChecked()

    return twMerge('absolute rounded-full transition-all duration-200 ease-in-out transform scale-0', checked ? 'scale-100' : '', size.dot, color.dot)
  })

  const labelClasses = createMemo(() => twMerge(currentSizeClasses().label, 'text-foreground select-none transition-colors duration-200', others.disabled ? 'opacity-50' : ''))

  const labelContainerClasses = createMemo(() => twMerge('inline-flex items-center cursor-pointer group', currentSizeClasses().spacing))

  return (
    <div class="inline-flex flex-col">
      <label for={uid} class={labelContainerClasses()}>
        <div class={containerClasses()} classList={local.classList}>
          <input
            ref={inputRef}
            id={uid}
            type="radio"
            class="absolute h-full w-full cursor-pointer opacity-0"
            {...(others.checked !== undefined ? { checked: others.checked } : {})}
            onChange={handleInputChange}
            disabled={others.disabled}
            aria-describedby={`${uid}-message`}
            aria-invalid={hasError() ? 'true' : undefined}
            {...others}
          />
          <div class={dotClasses()} />
        </div>
        <Show when={local.label}>
          <span class={labelClasses()}>{local.label}</span>
        </Show>
      </label>
      {/* Antes el mensaje se perdía: el error se convertía a booleano y sólo
          quedaba el borde en rojo, así que un campo obligatorio no decía por qué. */}
      <FieldMessage id={`${uid}-message`} size={local.size} indent={false} reserve={false} error={resolvedError()} helperText={local.helperText} class={others.disabled ? 'opacity-50' : ''} />
    </div>
  )
}
